import React from "react"
import { Box, Grid, Typography } from "@mui/material"
import CommonModal from "components/CommonModal"
import AppButton from "components/StyledComponents/AppButton"
import AppTextField from "components/StyledComponents/AppTextField"
import AppDatePicker from "components/AppDatePicker"
import StyledLabel from "components/StyledComponents/StyledLabel"

// formik stores time as "HH:mm" to match the native time input used across the platform.
// Dates/times are entered and stored in UTC, so "Now" fills the current UTC time.
const nowHHmm = () => {
  const d = new Date()
  const pad = (n) => String(n).padStart(2, "0")
  return `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`
}

const ShoppingSpreeForm = ({ open, onClose, formik, isEdit = false, loading = false }) => {
  return (
    <CommonModal
      open={open}
      onClose={onClose}
      maxWidth="md"
      actionButtons={
        <Box sx={{ display: "flex", justifyContent: "center", gap: 2, pb: 2 }}>
          <AppButton onClick={formik.handleSubmit} disabled={loading}>
            {loading ? "Saving..." : isEdit ? "Update" : "Save"}
          </AppButton>
        </Box>
      }>
      <Typography variant="h2" textAlign="center" mb={1}>
        {isEdit ? "Edit Shopping Spree Window" : "Add Shopping Spree Window"}
      </Typography>
      <Typography variant="body2Regular" color="text.secondary" textAlign="center" mb={3}>
        Note: All dates and times are in UTC — please input in UTC.
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6} className="datesContainer">
          <StyledLabel variant="body1Regular">
            Start Date <span>*</span>
          </StyledLabel>
          <Box className="dateFieldContainer w-100 mt-2">
            <AppDatePicker
              minDate={new Date()}
              value={formik.values.shopping_spree_start_date}
              onChange={(val) => formik.setFieldValue("shopping_spree_start_date", val)}
              inputFormat="dd MMM yyyy"
              disableMaskedInput
              fullWidth
              error={formik.errors.shopping_spree_start_date}
              touched={formik.touched.shopping_spree_start_date}
            />
          </Box>
        </Grid>
        <Grid item xs={12} md={6} className="datesContainer">
          <StyledLabel variant="body1Regular">
            Start Time <span>*</span>
          </StyledLabel>
          <Box className="mt-2" sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
            <Box sx={{ flex: 1 }}>
              <AppTextField
                fullWidth
                id="shopping_spree_start_time"
                name="shopping_spree_start_time"
                type="time"
                value={formik.values.shopping_spree_start_time}
                onChange={formik.handleChange("shopping_spree_start_time")}
                error={
                  formik.touched.shopping_spree_start_time &&
                  Boolean(formik.errors.shopping_spree_start_time)
                }
                helperText={
                  formik.touched.shopping_spree_start_time &&
                  formik.errors.shopping_spree_start_time
                }
              />
            </Box>
            <AppButton
              type="button"
              styles={{ height: 50, padding: "0 18px", minWidth: "auto", whiteSpace: "nowrap" }}
              onClick={() => formik.setFieldValue("shopping_spree_start_time", nowHHmm())}>
              Now
            </AppButton>
          </Box>
        </Grid>

        <Grid item xs={12} md={6} className="datesContainer">
          <StyledLabel variant="body1Regular">
            End Date <span>*</span>
          </StyledLabel>
          <Box className="dateFieldContainer w-100 mt-2">
            <AppDatePicker
              minDate={formik.values.shopping_spree_start_date}
              value={formik.values.shopping_spree_end_date}
              onChange={(val) => formik.setFieldValue("shopping_spree_end_date", val)}
              inputFormat="dd MMM yyyy"
              disableMaskedInput
              error={formik.errors.shopping_spree_end_date}
              touched={formik.touched.shopping_spree_end_date}
            />
          </Box>
        </Grid>
        <Grid item xs={12} md={6} className="datesContainer">
          <StyledLabel variant="body1Regular">
            End Time <span>*</span>
          </StyledLabel>
          <Box className="mt-2" sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
            <Box sx={{ flex: 1 }}>
              <AppTextField
                fullWidth
                id="shopping_spree_end_time"
                name="shopping_spree_end_time"
                type="time"
                value={formik.values.shopping_spree_end_time}
                onChange={formik.handleChange("shopping_spree_end_time")}
                error={
                  formik.touched.shopping_spree_end_time &&
                  Boolean(formik.errors.shopping_spree_end_time)
                }
                helperText={
                  formik.touched.shopping_spree_end_time && formik.errors.shopping_spree_end_time
                }
              />
            </Box>
            <AppButton
              type="button"
              styles={{ height: 50, padding: "0 18px", minWidth: "auto", whiteSpace: "nowrap" }}
              onClick={() => formik.setFieldValue("shopping_spree_end_time", nowHHmm())}>
              Now
            </AppButton>
          </Box>
        </Grid>

        <Grid item xs={12} md={6}>
          <AppTextField
            fullWidth
            id="max_rewards_per_user"
            name="max_rewards_per_user"
            type="number"
            label="Max Rewards/User"
            isUpperCase={true}
            isRequired
            placeholder="Enter Max Rewards/User"
            value={formik.values.max_rewards_per_user}
            onChange={formik.handleChange}
            error={
              formik.touched.max_rewards_per_user && Boolean(formik.errors.max_rewards_per_user)
            }
            helperText={formik.touched.max_rewards_per_user && formik.errors.max_rewards_per_user}
          />
        </Grid>
      </Grid>
    </CommonModal>
  )
}

export default ShoppingSpreeForm
